/**
 * NukkiEngine — 편집 세션 하나.
 *
 * UI가 없다. DOM 이벤트를 달지 않고, 전역에 손대지 않으며, 렌더 타이밍을
 * 결정하지 않는다 (호스트가 rAF로 `composite()`를 부른다).
 *
 * 설계문서 §5.2 (합성 렌더), §5.4 (Surface), §5.5 (export), §5.6 (적응형 해상도)
 */
import { applyOp, applyStroke, buildSelection, rebuild, SUBJECT_THRESHOLD } from './ops.js';
import { canvas2dSurfaces } from './canvas2d.js';
import type {
  ExportOptions, LassoMode, MaskBackend, Op, Point, SelectStroke,
  Surface, SurfaceFactory, SurfaceImage,
} from './types.js';

export interface NukkiEngineOptions {
  backend: MaskBackend;
  /** 미지정 시 Canvas 2D. */
  surfaces?: SurfaceFactory;
  /** 편집 해상도 상한. 미지정 시 적응형 (§5.6). */
  workingSize?: number;
  /** 명령 로그 상한. 넘으면 앞쪽을 픽셀로 굽는다. */
  maxOps?: number;
}

/**
 * 적응형 작업 해상도 (§5.6).
 *
 * 모바일 WebView는 iOS 캔버스 메모리 총량 상한 때문에 낮춘다.
 * 편집만 저해상도로 하고 export에서 원본까지 올리므로 화질 손실은 없다.
 */
export function pickWorkingSize(coarsePointer?: boolean): number {
  const coarse = coarsePointer ?? (
    typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches
  );
  return coarse ? 1400 : 2400;
}

type EventName = 'load' | 'change' | 'busy' | 'aimask' | 'selection';

/** 선택 밖을 덮는 색. 너무 어두우면 무엇이 잘리는지 안 보인다. */
const SELECTION_DIM = 'rgba(9, 11, 16, 0.66)';

/* ── marching ants ──────────────────────────────────────────────
   경계는 **래스터에서 뽑는다.** 올가미 다각형을 그리면 AI 보정·추가·빼기가
   반영되지 않은 엉뚱한 선이 나온다. 실제 선택 모양을 보여줘야 한다.
   ─────────────────────────────────────────────────────────────── */
const ANT_PERIOD = 12;     // 점선 주기(px)
const ANT_GAP = 6;         // 그중 빈 구간
const ANT_DARK = 'rgba(0, 0, 0, 0.85)';
const ANT_LIGHT = '#ffffff';

export class NukkiEngine extends EventTarget {
  readonly backend: MaskBackend;
  readonly surfaces: SurfaceFactory;
  readonly workingMax: number;
  readonly maxOps: number;

  /** 화면에 붙일 서피스. Canvas2D면 `.canvas`로 엘리먼트를 꺼낸다. */
  readonly view: Surface;

  private mask: Surface | null = null;
  private ai: Surface | null = null;
  private base: Surface | null = null;      // 베이크된 시작 상태 (지연 생성)
  private scratch: Surface | null = null;   // 올가미 ∩ AI 조합용 (지연 생성)

  /* ── 선택 영역 ────────────────────────────────────────────────
     마스크와 **별개**다. 사용자가 여러 번 더하고 빼서 원하는 영역을
     만든 뒤 applySelection() 으로 한 번에 마스크에 반영한다.
     그래서 올가미를 떼는 순간 이미지가 잘리지 않는다.
     ─────────────────────────────────────────────────────────── */
  private selection: Surface | null = null;   // 지연 생성
  private overlay: Surface | null = null;     // 선택 밖을 어둡게 덮는 용도
  private edge: Surface | null = null;        // 선택 경계선(marching ants)
  private selStrokes: SelectStroke[] = [];
  /** 클릭 선택용 AI 알파. **이미지당 1회만** 읽는다 (설계문서 C-1). */
  private aiAlpha: Uint8Array | null = null;

  private src: SurfaceImage | null = null;
  private srcBlob: Blob | null = null;      // 서버로 그대로 보낸다 (재인코딩 금지)
  private w = 0;
  private h = 0;

  private ops: Op[] = [];
  private redos: Op[] = [];

  /** 세대 토큰. `await` 이후 값이 바뀌었으면 그 작업 결과는 버린다 (§4.1 경쟁 상태). */
  private gen = 0;
  private abort: AbortController | null = null;
  private aiPromise: Promise<Surface | null> | null = null;
  private busyCount = 0;
  private destroyed = false;

  constructor({ backend, surfaces, workingSize, maxOps = 200 }: NukkiEngineOptions) {
    super();
    if (!backend) throw new Error('backend가 필요합니다');
    this.backend = backend;
    this.surfaces = surfaces ?? canvas2dSurfaces();
    this.workingMax = workingSize ?? pickWorkingSize();
    this.maxOps = maxOps;
    this.view = this.surfaces.create(1, 1);
  }

  /* ── 상태 ────────────────────────────────────────────────── */

  get loaded(): boolean { return this.src !== null; }
  get busy(): boolean { return this.busyCount > 0; }
  get canUndo(): boolean { return this.ops.length > 0; }
  get canRedo(): boolean { return this.redos.length > 0; }
  get hasAiMask(): boolean { return this.ai !== null; }
  /** 확정 대기 중인 선택 영역이 있는가. 있으면 화면은 원본 + 선택 하이라이트다. */
  get hasSelection(): boolean { return this.selStrokes.length > 0; }
  get selectionSteps(): number { return this.selStrokes.length; }
  get width(): number { return this.w; }
  get height(): number { return this.h; }
  get opCount(): number { return this.ops.length; }
  /** 원본 픽셀 크기. 편집은 작업 해상도지만 export는 여기까지 올릴 수 있다. */
  get sourceSize(): { w: number; h: number } | null {
    return this.src ? { w: this.src.width, h: this.src.height } : null;
  }

  private emit(type: EventName, detail?: unknown): void {
    if (!this.destroyed) this.dispatchEvent(new CustomEvent(type, { detail }));
  }

  private async withBusy<T>(fn: () => Promise<T>): Promise<T> {
    this.busyCount++;
    this.emit('busy', { busy: true });
    try {
      return await fn();
    } finally {
      this.busyCount--;
      this.emit('busy', { busy: this.busyCount > 0 });
    }
  }

  /* ── 로드 ────────────────────────────────────────────────── */

  /** 이미지를 읽고 작업 서피스를 세운다. 이전 이미지의 in-flight 요청은 전부 무효화된다. */
  async load(blob: Blob): Promise<boolean> {
    const gen = ++this.gen;
    this.abort?.abort();
    this.abort = new AbortController();
    this.aiPromise = null;

    let img: SurfaceImage;
    try {
      img = await this.surfaces.decode(blob);
    } catch (e) {
      throw new Error(`이미지를 읽을 수 없습니다: ${(e as Error).message}`);
    }
    if (gen !== this.gen) { img.close?.(); return false; }

    this.src?.close?.();
    this.src = img;
    this.srcBlob = blob;

    this.ai?.dispose(); this.ai = null;
    this.aiAlpha = null;
    this.base?.dispose(); this.base = null;
    if (this.scratch) this.resizeSurface(this.scratch, 1, 1);   // 크기는 아래에서 다시

    const s = Math.min(1, this.workingMax / Math.max(img.width, img.height));
    this.w = Math.max(1, Math.round(img.width * s));
    this.h = Math.max(1, Math.round(img.height * s));

    this.resizeSurface(this.view, this.w, this.h);
    if (this.mask) this.resizeSurface(this.mask, this.w, this.h);
    else this.mask = this.surfaces.create(this.w, this.h);
    if (this.scratch) this.resizeSurface(this.scratch, this.w, this.h);

    this.ops = [];
    this.redos = [];
    this.selStrokes = [];
    if (this.selection) this.resizeSurface(this.selection, this.w, this.h);
    if (this.overlay) this.resizeSurface(this.overlay, this.w, this.h);
    if (this.edge) this.resizeSurface(this.edge, this.w, this.h);
    rebuild(this.mask, this.ops, this.ai, this.base, this.scratchForOps(this.ops), this.aiAlpha);
    this.composite();
    this.emit('load', { width: this.w, height: this.h });
    this.emit('change');
    return true;
  }

  /**
   * 올가미 합성용 스크래치. `add`/`replace` + `refine` 에서만 필요하므로
   * 그때 처음 만든다 -- 자동 누끼만 쓰는 소비처는 캔버스를 하나 덜 든다.
   */
  private scratchFor(): Surface | null {
    if (!this.mask) return null;
    if (!this.scratch) this.scratch = this.surfaces.create(this.w, this.h);
    return this.scratch;
  }

  /** 이 명령이 스크래치를 실제로 쓰는가. 안 쓰면 만들지 않는다. */
  private static needsScratch(op: Op): boolean {
    if (op.t !== 'select') return false;
    return op.strokes.some(
      (st) => st.kind === 'point'
        || (st.kind === 'lasso' && st.mode !== 'subtract' && st.refine),
    );
  }

  /** 명령 하나에 필요한 스크래치. 필요 없으면 null (캔버스를 만들지 않는다). */
  /** 클릭 선택에 쓸 AI 알파. 처음 필요할 때 한 번만 읽는다. */
  private aiAlphaFor(): Uint8Array | null {
    if (!this.ai) return null;
    if (!this.aiAlpha) this.aiAlpha = this.ai.readAlpha();
    return this.aiAlpha;
  }

  private selectionSurface(): Surface {
    if (!this.selection) this.selection = this.surfaces.create(this.w, this.h);
    return this.selection;
  }

  private scratchIfNeeded(op: Op): Surface | null {
    return NukkiEngine.needsScratch(op) && this.ai ? this.scratchFor() : null;
  }

  /** 로그 전체를 훑어 하나라도 필요하면 준비한다. */
  private scratchForOps(ops: readonly Op[]): Surface | null {
    return this.ai && ops.some(NukkiEngine.needsScratch) ? this.scratchFor() : null;
  }

  /** 서피스가 resize를 지원하면 재사용, 아니면 새로 만든다 (캔버스 개수를 상수로 유지). */
  private resizeSurface(s: Surface, w: number, h: number): void {
    const r = (s as Surface & { resize?: (w: number, h: number) => void }).resize;
    if (typeof r === 'function') r.call(s, w, h);
  }

  /* ── AI 마스크 (이미지당 1회) ─────────────────────────────── */

  /** 백그라운드 선취. 실패해도 조용히 -- 실제로 필요할 때 다시 시도된다. */
  prefetchMask(): void { void this.ensureAiMask().catch(() => {}); }

  async ensureAiMask(): Promise<Surface | null> {
    if (this.ai) return this.ai;
    if (this.aiPromise) return this.aiPromise;
    if (!this.src || !this.srcBlob) throw new Error('이미지가 없습니다');

    const gen = this.gen;
    const signal = this.abort!.signal;
    const blob = this.srcBlob;

    const p = this.withBusy(async () => {
      const img = await this.backend.getMask(blob, { signal });
      // 경쟁 상태 방어: 이 검사가 없으면 사진 A의 마스크가 B에 적용된다.
      if (gen !== this.gen) { img.close?.(); return null; }

      const ai = this.surfaces.create(this.w, this.h);
      ai.drawImage(img, 'srcOver', { w: this.w, h: this.h });
      img.close?.();
      this.ai = ai;
      this.aiAlpha = null;          // 마스크가 바뀌었으니 다시 읽어야 한다
      this.emit('aimask');
      return ai;
    });
    this.aiPromise = p;

    try {
      const r = await p;
      // 필드가 아직 우리 것일 때만 지운다. 그 사이 load()가 새 요청을 걸었다면
      // 남의 promise를 null로 만들어 중복 요청을 유발한다.
      if (r === null && this.aiPromise === p) this.aiPromise = null;
      return r;
    } catch (e) {
      if (this.aiPromise === p) this.aiPromise = null;
      throw e;
    }
  }

  /* ── 명령 로그 ───────────────────────────────────────────── */

  private push(op: Op): void {
    // 베이크는 push 전에. 순서를 바꾸면 rebuild가 새 명령까지 적용한 뒤
    // 아래에서 한 번 더 적용해 이중 반영된다.
    if (this.ops.length + 1 > this.maxOps) {
      this.bake(this.ops.length - Math.floor(this.maxOps / 2));
    }
    this.ops.push(op);
    this.redos.length = 0;
    applyOp(this.mask!, op, this.ai, this.scratchIfNeeded(op), this.aiAlpha);
    this.composite();
    this.emit('change');
  }

  /** 앞쪽 k개를 픽셀로 구워 로그에서 없앤다. 결과는 동일, 로그만 짧아진다. */
  private bake(k: number): void {
    const tail = this.ops.slice(k);
    this.ops.length = k;
    rebuild(this.mask!, this.ops, this.ai, this.base, this.scratchForOps(this.ops), this.aiAlpha);
    if (!this.base) this.base = this.surfaces.create(this.w, this.h);
    this.base.drawImage(this.mask!.asImage(), 'copy');
    this.ops = tail;
    rebuild(this.mask!, this.ops, this.ai, this.base, this.scratchForOps(this.ops), this.aiAlpha);   // tail을 새 base 위에 다시
  }

  /* ── 편집 연산 ───────────────────────────────────────────── */

  /** 전체 자동: 마스크 := AI 마스크 */
  async autoCutout(): Promise<boolean> {
    const gen = this.gen;
    await this.ensureAiMask();
    if (gen !== this.gen || !this.ai) return false;
    this.push({ t: 'auto' });
    return true;
  }

  reset(): void {
    this.clearSelection();
    this.push({ t: 'reset' });
  }

  /* ── 선택 영역 (에디터의 기본 흐름) ──────────────────────────
       영역 자동맞춤 / 올가미 → 선택 다듬기 → 적용 → 잘림
     ─────────────────────────────────────────────────────────── */

  /** 선택 영역을 AI 마스크로 채운다. **이미지는 아직 잘리지 않는다.** */
  async autoSelect(mode: LassoMode = 'replace'): Promise<boolean> {
    const gen = this.gen;
    await this.ensureAiMask();
    if (gen !== this.gen || !this.ai) return false;
    return this.pushStroke({ kind: 'auto', mode, refine: true });
  }

  /**
   * 올가미로 선택 영역을 만들거나 다듬는다. **이미지는 아직 잘리지 않는다.**
   *
   * `subtract` 는 AI 마스크가 필요 없으므로 서버를 기다리지 않는다 --
   * 오프라인·서버다운에서도 지울 수 있어야 한다.
   */
  async selectLasso(
    pts: readonly Point[],
    { refine = true, mode = 'replace' as LassoMode } = {},
  ): Promise<boolean> {
    if (!pts || pts.length < 3) return false;
    const gen = this.gen;
    if (refine && mode !== 'subtract') await this.ensureAiMask();
    if (gen !== this.gen) return false;
    return this.pushStroke({
      kind: 'lasso',
      pts: pts.map((p) => [p[0], p[1]] as Point),
      mode,
      refine: refine && mode !== 'subtract' && !!this.ai,
    });
  }

  /**
   * **누른 지점이 속한 덩어리만** 선택한다.
   *
   * AI 마스크에서 연결 영역을 찾으므로 사진에 피사체가 여럿이면 누른 것만 잡힌다.
   * 서버를 다시 부르지 않는다 — 마스크는 이미 받아 뒀다.
   *
   * @returns 배경을 눌렀으면 `false`.
   */
  async selectAt(
    x: number,
    y: number,
    { mode = 'replace' as LassoMode } = {},
  ): Promise<boolean> {
    const gen = this.gen;
    await this.ensureAiMask();
    if (gen !== this.gen || !this.ai) return false;
    const alpha = this.aiAlphaFor();
    if (!alpha) return false;
    const xi = Math.round(x), yi = Math.round(y);
    if (xi < 0 || yi < 0 || xi >= this.w || yi >= this.h) return false;
    if (alpha[yi * this.w + xi]! < SUBJECT_THRESHOLD) return false;   // 배경
    return this.pushStroke({ kind: 'point', pts: [[xi, yi]], mode, refine: true });
  }

  private pushStroke(stroke: SelectStroke): boolean {
    if (!this.src) return false;
    this.selStrokes.push(stroke);
    applyStroke(this.selectionSurface(), stroke, this.ai, this.scratchFor(), this.aiAlphaFor());
    this.composite();
    this.emit('selection', { steps: this.selStrokes.length });
    return true;
  }

  /** 선택의 마지막 동작만 취소한다. 잘못 그은 획 하나를 물릴 때. */
  undoSelectionStep(): boolean {
    if (!this.selStrokes.length) return false;
    this.selStrokes.pop();
    if (this.selStrokes.length) {
      buildSelection(this.selectionSurface(), this.selStrokes, this.ai, this.scratchFor(), this.aiAlphaFor());
    } else {
      this.selection?.clear();
    }
    this.composite();
    this.emit('selection', { steps: this.selStrokes.length });
    return true;
  }

  /** 선택을 통째로 버린다. 이미지는 건드리지 않는다. */
  clearSelection(): boolean {
    if (!this.selStrokes.length) return false;
    this.selStrokes = [];
    this.selection?.clear();
    this.composite();
    this.emit('selection', { steps: 0 });
    return true;
  }

  /** 선택을 마스크에 반영한다. **여기서 처음으로 이미지가 잘린다.** */
  applySelection(): boolean {
    if (!this.selStrokes.length) return false;
    const strokes = this.selStrokes;
    this.selStrokes = [];
    this.selection?.clear();
    this.push({ t: 'select', strokes });
    this.emit('selection', { steps: 0 });
    return true;
  }

  /* ── undo / redo ─────────────────────────────────────────── */

  undo(): boolean {
    if (!this.ops.length) return false;
    this.redos.push(this.ops.pop()!);
    rebuild(this.mask!, this.ops, this.ai, this.base, this.scratchForOps(this.ops), this.aiAlpha);
    this.composite();
    this.emit('change');
    return true;
  }

  redo(): boolean {
    if (!this.redos.length) return false;
    this.ops.push(this.redos.pop()!);
    rebuild(this.mask!, this.ops, this.ai, this.base, this.scratchForOps(this.ops), this.aiAlpha);
    this.composite();
    this.emit('change');
    return true;
  }

  /* ── 렌더 (§5.2) ─────────────────────────────────────────── */

  /**
   * 뷰 서피스를 다시 합성한다. ImageData 할당 0, 픽셀 루프 0.
   *
   * 올가미 미리보기 같은 장식은 그리지 않는다 -- 그건 뷰 층의 몫이다.
   * 호스트가 rAF 안에서 이걸 부르고 그 위에 자기 장식을 얹는다.
   */
  /**
   * 선택을 **근사 이진화**한다.
   *
   * ⚠️ 이게 없으면 경계선이 테두리가 아니라 반투명 영역 전체에 깔린다.
   * AI 마스크는 부드러운 알파를 갖는데, 팽창 후 원본을 빼면
   * `a × (1 − a)` 가 남는다 — a=0.5 면 0.25 가 내부에 남아 사선이 비친다.
   *
   * `srcOver` 로 여러 번 겹치면 `1 − (1−a)ⁿ` 이라 중간값이 1 로 밀린다.
   * 픽셀 배열을 읽지 않으므로 iOS 캔버스 예산에도 안전하다.
   */
  private solidify(sel: Surface): SurfaceImage {
    const tmp = this.scratchFor()!;
    tmp.clear();
    const img = sel.asImage();
    for (let i = 0; i < 12; i++) tmp.drawImage(img, 'srcOver');
    return tmp.asImage();
  }

  /**
   * 선택 경계선을 그린다. 팽창(dilate) 후 원본을 빼면 테두리가 남는다.
   * 거기에 사선 띠를 `dstOut` 으로 뚫으면 점선이 되고, `phase` 를 흘리면 움직인다.
   */
  private drawAnts(img: SurfaceImage, thickness: number, color: string, phase: number): void {
    if (!this.edge) this.edge = this.surfaces.create(this.w, this.h);
    const e = this.edge;
    const t = thickness;

    e.clear();
    // 상하좌우로 밀어 그려 두께 t 만큼 부풀린다 (대각까지 하면 8회지만 4회로 충분).
    for (const [dx, dy] of [[-t, 0], [t, 0], [0, -t], [0, t]] as const) {
      e.drawImage(img, 'srcOver', { x: dx, y: dy, w: this.w, h: this.h });
    }
    e.drawImage(img, 'dstOut');            // 원본을 빼면 테두리만 남는다

    // 사선 띠를 뚫어 점선으로. phase 가 흐르면 개미가 행진한다.
    const off = ((phase % ANT_PERIOD) + ANT_PERIOD) % ANT_PERIOD;
    for (let c = -this.h - ANT_PERIOD + off; c < this.w + this.h; c += ANT_PERIOD) {
      e.fillPath(
        [[c, 0], [c + ANT_GAP, 0], [c + ANT_GAP - this.h, this.h], [c - this.h, this.h]],
        'dstOut',
      );
    }

    // ⚠️ dstIn 이 아니라 srcIn 이다. dstIn 은 목적지 색을 유지하므로
    //    선택(흰색)이 그대로 나와 어두운 선이 흰색이 된다.
    e.fillAll('srcIn', color);             // 모양은 두고 색만 갈아끼운다
    this.view.drawImage(e.asImage(), 'srcOver');
  }

  /**
   * @param antsPhase 경계선 애니메이션 위상. 호스트가 rAF 로 흘려 준다
   *   (렌더 타이밍은 뷰 책임이라 코어가 타이머를 돌리지 않는다).
   */
  composite(antsPhase = 0): void {
    if (!this.src || this.destroyed || !this.mask) return;
    this.view.clear();
    this.view.drawImage(this.src, 'srcOver', { w: this.w, h: this.h });

    if (this.selStrokes.length && this.selection) {
      // 선택 중에는 **이미지를 자르지 않는다.** 원본을 그대로 두고
      // 선택 밖을 어둡게 덮어 "무엇이 남을지"를 보여준다.
      if (!this.overlay) this.overlay = this.surfaces.create(this.w, this.h);
      this.overlay.fillAll('copy', SELECTION_DIM);
      this.overlay.drawImage(this.selection.asImage(), 'dstOut');   // 선택 영역만 뚫는다
      this.view.drawImage(this.overlay.asImage(), 'srcOver');

      // 이진화는 한 번만 하고 두 선이 같은 모양을 공유한다.
      const solid = this.solidify(this.selection);
      // 밝은 배경에서도 보이도록 어두운 선을 먼저 깔고 흰 선을 얹는다.
      // 얇게. 어두운 선이 흰 선의 그림자 역할을 해 밝은 배경에서도 보인다.
      this.drawAnts(solid, 2, ANT_DARK, antsPhase);
      this.drawAnts(solid, 1, ANT_LIGHT, antsPhase + ANT_PERIOD / 2);
      return;
    }

    // 마스크 알파를 곱해 배경을 투명하게. 브라우저/GPU 합성기가 처리한다.
    this.view.drawImage(this.mask.asImage(), 'dstIn');
  }

  /* ── 내보내기 (§5.5) ─────────────────────────────────────── */

  async exportBlob(opts: ExportOptions = {}): Promise<Blob> {
    if (!this.src || !this.mask) throw new Error('이미지가 없습니다');
    const { resolution = 'working', format = 'png', quality = 0.92 } = opts;
    const ow = this.src.width, oh = this.src.height;

    let tw: number, th: number;
    if (resolution === 'original') {
      tw = ow; th = oh;
    } else if (typeof resolution === 'number') {
      const s = Math.min(1, resolution / Math.max(ow, oh));
      tw = Math.max(1, Math.round(ow * s));
      th = Math.max(1, Math.round(oh * s));
    } else {
      tw = this.w; th = this.h;
    }

    const out = this.surfaces.create(tw, th);
    try {
      out.drawImage(this.src, 'srcOver', { w: tw, h: th });
      // 작업 해상도 마스크를 목표 해상도로 업스케일해 합성 -> 원본 화질 유지
      out.drawImage(this.mask.asImage(), 'dstIn', { w: tw, h: th });
      return await out.encode(format, quality);
    } finally {
      out.dispose();   // 원본 해상도 서피스는 클 수 있다. 즉시 반납.
    }
  }

  /* ── 정리 ────────────────────────────────────────────────── */

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.gen++;
    this.abort?.abort();
    this.src?.close?.();
    this.src = null;
    this.srcBlob = null;
    this.mask?.dispose();
    this.ai?.dispose();
    this.base?.dispose();
    this.scratch?.dispose();
    this.selection?.dispose();
    this.overlay?.dispose();
    this.edge?.dispose();
    this.view.dispose();
    this.mask = this.ai = this.base = this.scratch = null;
    this.selection = this.overlay = this.edge = null;
    this.selStrokes = [];
    this.ops = [];
    this.redos = [];
  }
}
